import React, { useState, useEffect, useCallback } from 'react';
import { reactToVideo, getVideoReactions } from '../services/api';
import { authService } from '../services/authService';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import './VideoReaction.css';

/**
 * VideoReaction — colored smiley rating bar shown under a video.
 *
 * Props:
 *  videoId       — numeric video id (required)
 *  showCounts    — if true, show aggregate community counts (public gallery mode)
 *                  if false (default), show only the user's own selection (course mode)
 *  compact       — smaller layout for card views
 */
export default function VideoReaction({ videoId, showCounts = false, compact = false }) {
  const { language } = useLanguage();
  const tStudent = (translations[language]?.studio?.student) || {};
  const REACTIONS = [
    { key: 'great',    emoji: '😄', color: '#22c55e', label: tStudent.reactionGotIt    || 'Got it!' },
    { key: 'good',     emoji: '🙂', color: '#eab308', label: tStudent.reactionMostly   || 'Mostly got it' },
    { key: 'confused', emoji: '😕', color: '#f97316', label: tStudent.reactionNotQuite || 'Not quite' },
    { key: 'lost',     emoji: '😟', color: '#ef4444', label: tStudent.reactionLost     || 'Lost' },
  ];

  const [myReaction, setMyReaction] = useState(null);
  const [counts, setCounts]         = useState({});
  const [saving, setSaving]         = useState(false);
  const isLoggedIn = authService.isAuthenticated();

  const load = useCallback(async () => {
    if (!videoId) return;
    try {
      const data = await getVideoReactions(videoId);
      setCounts(data.counts || {});
      setMyReaction(data.my_reaction || null);
    } catch (_) {}
  }, [videoId]);

  useEffect(() => { load(); }, [load]);

  const handleReact = async (key) => {
    if (!isLoggedIn || saving) return;
    setSaving(true);
    const prev = myReaction;
    setMyReaction(key); // optimistic
    try {
      await reactToVideo(videoId, key);
      await load(); // refresh counts
    } catch (_) {
      setMyReaction(prev); // rollback
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`video-reaction${compact ? ' video-reaction--compact' : ''}`}>
      <span className="video-reaction__label">
        {isLoggedIn ? (tStudent.howWellDidYouUnderstand || 'How well did you understand?') : (tStudent.communityRating || 'Community rating')}
      </span>

      <div className="video-reaction__faces">
        {REACTIONS.map(({ key, emoji, color, label }) => {
          const count = counts[key] || 0;
          const isSelected = myReaction === key;
          return (
            <button
              key={key}
              className={`vr-face${isSelected ? ' vr-face--selected' : ''}`}
              style={{ '--face-color': color }}
              onClick={() => handleReact(key)}
              disabled={!isLoggedIn || saving}
              title={label}
              aria-label={label}
            >
              <span className="vr-circle">{emoji}</span>
              {showCounts && count > 0 && (
                <span className="vr-count">{count > 99 ? '99+' : count}</span>
              )}
            </button>
          );
        })}
      </div>

      {!isLoggedIn && (
        <span className="video-reaction__hint">{tStudent.signInToRate || 'Sign in to rate'}</span>
      )}
    </div>
  );
}
